import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, NonIdealState, Button } from '@blueprintjs/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
// @ts-ignore
import { sortableHandle } from 'react-sortable-hoc';
import { DeletePopover } from '@hogwarts/ui-components-core';

import './styles.css';
import styles from './styles.module.css';

const DraggableHeader = sortableHandle(({ title }: { title: string }) => (
  <div className={styles.dragContainer}>
    {title && <p className={styles.headerTitle}>{title}</p>}
  </div>
));

interface Props {
  title: string;
  loading?: boolean;
  refreshing?: boolean;
  recalculating?: boolean;
  error?: boolean;
  unavailable?: boolean;
  empty?: boolean;
  message?: string;
  children: React.ReactNode;
  onSettingsClick?: () => any;
  onRemoveClick?: () => any;
  disableScroll?: boolean;
}

const WidgetWrapper = ({
  title,
  loading,
  refreshing,
  recalculating,
  error,
  unavailable,
  empty,
  message = '',
  children: widget,
  onSettingsClick,
  onRemoveClick,
  disableScroll,
}: Props) => {
  const { t } = useTranslation();

  const showInitialLoading = loading && !refreshing && !recalculating;
  // Background poll refresh should keep showing existing data; only block for recalc.
  const showProgressState = !error && !!recalculating;
  const showEmpty = !error && !unavailable && !showProgressState && !!empty;

  return (
    <li className={styles.widgetListContainer}>
      <div className={cn('widget-container', styles.widgetWrapperContainer)}>
        <div className={cn('widget-header', styles.widgetHeader)}>
          <DraggableHeader title={title} />
          <div>
            {refreshing && !recalculating && !error && (
              <Spinner size={16} intent="primary" className="mr-1" />
            )}
            {onSettingsClick && (
              <Button
                small
                minimal
                icon={<FontAwesomeIcon icon={['far', 'cog']} />}
                onClick={onSettingsClick}
              />
            )}
            {onRemoveClick && (
              <DeletePopover
                buttonComponent={
                  <Button
                    small
                    minimal
                    icon={<FontAwesomeIcon icon={['far', 'trash-alt']} />}
                  />
                }
                message="Are you sure you want to remove this widget?"
                onRemove={onRemoveClick}
              />
            )}
          </div>
        </div>
        {showInitialLoading && (
          <NonIdealState
            icon={<Spinner intent="primary" />}
            title={t('Loading dashboard...')}
            description={message ? t<string>(message) : undefined}
          />
        )}
        <div
          className={cn(
            styles.widgetContent,
            !disableScroll && styles.widgetContentScroll
          )}
        >
          {error && (
            <NonIdealState
              icon="error"
              title={t('Error Loading Widget')}
              description={t<string>(
                message ||
                  'Recalculation failed. Please try again or contact support.'
              )}
            />
          )}
          {!showInitialLoading && !error && unavailable && (
            <NonIdealState
              icon="warning-sign"
              title={t('Widget Unavailable')}
              description={t<string>(message)}
            />
          )}
          {!showInitialLoading && !error && !unavailable && showProgressState && (
            <NonIdealState
              icon={<Spinner intent="primary" />}
              title={t('Recalculation in progress...')}
              description={t<string>(
                message ||
                  'Ratings are being recalculated. This usually takes a moment.'
              )}
            />
          )}
          {!showInitialLoading && !error && !unavailable && showEmpty && (
            <NonIdealState
              icon="info-sign"
              title={t('Nothing to show')}
              description={t<string>(
                message ||
                  'Nothing to display for the current widget settings.'
              )}
            />
          )}
          {!showInitialLoading &&
            !error &&
            !unavailable &&
            !showProgressState &&
            !showEmpty &&
            widget}
        </div>
      </div>
    </li>
  );
};

export default WidgetWrapper;
